import React, { useMemo, useState } from 'react';
import { useIntl } from 'umi';
import {
  channelColorMap,
  channelNameMap,
  metricsMap,
  metricsOptions,
  titleMap,
} from '@op/pages/community/constants';
import BubbleCommon, {
  communityStreamingAvgText,
  communityStreamingPeakCCVMaxText,
} from '@/pages/opinion/components/BubbleCommon';
import {
  echartCountMat,
  mathFormat,
} from '@/pages/opinion/utils';
import CardCommon from '@/pages/opinion/components/CardCommon';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@/pages/opinion/components/ChartCommon/formatter';
import { channelInfo } from '@/pages/opinion/components/ChannelLabel';
import { downloadAction } from '@/pages/opinion/components/DownloadCommon/download-page';
import DownloadDialog from '@op/components/DownloadCommon/download-dialog';
import { NO_DATE } from '@/pages/opinion/constants';
import LineChart from '@op/pages/community/components/LineChart';

export default function OpinionsStreamingChart(props: any) {
  const {
    metric,
    params,
    gameInfo,
    loading,
    total,
    lineSeriesData,
    lineXAxisData,
    title: propsTitle = "",
    metricsOptions: propsMetricsOptions,
  } = props || {};
  const { formatMessage } = useIntl();

  const title = useMemo(() => {
    const tips =
      metric === 'avgCCV'
        ? communityStreamingAvgText
        : metric === 'peakCCV'
          ? communityStreamingPeakCCVMaxText
          : null;

    return (
      <div className="trends-card-title">
        <span>{formatMessage({ id: 'Trends' })}</span>
        <BubbleCommon tipsMap={tips} showIcon={false}>
          <span className="trends-card-subtitle">
            ({formatMessage({ id: titleMap[metric] })}: {echartCountMat(total)})
          </span>
        </BubbleCommon>
      </div>
    );
  }, [metric, formatMessage]);


  const download = (downloadInfo: any = {}) => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const group_by: Array<string> = [];
    if (downloadInfo.accumulation?.by_date) {
      group_by.push('date');
    }
    if (downloadInfo.accumulation?.by_channel) {
      group_by.push('channel');
    }
    const newParams = {
      ...params,
      ...gameInfo,
      metric_list: downloadInfo.metrics,
      group_by,
      page: 1,
      source: 'stream',
      page_size: 1000,
    };
    downloadAction({
      limit: 150000,
      downLoadParams: newParams,
      downloadFunc: 'getStreamingDownloadTrends',
      sheetName: 'Trends',
      simpleName: 'Streaming',
    });
  };

  const [visible, setVisible] = useState(false);

  return (
    <>
      <DownloadDialog
        showList={[
          {
            label: 'Metrics',
            type: 'checkbox',
            list: propsMetricsOptions || metricsOptions,
          },
          {
            label: 'Calculation Method',
            type: 'radioGroup',
            list: [
              {
                label: 'Time Settings',
                list: [
                  {
                    text: 'Aggregated',
                    value: 'Aggregated',
                  },
                  {
                    text: 'Separate by Time',
                    value: 'Separate by Time',
                  },
                ],
              },
              {
                label: 'Channel Settings',
                list: [
                  {
                    text: 'Aggregated',
                    value: 'Aggregated',
                  },
                  {
                    text: 'Separate by Channel',
                    value: 'Separate by Channel',
                  },
                ],
              },
            ],
          },
        ]}
        visible={visible}
        setVisible={setVisible}
        download={download}
        gameInfo={gameInfo}
      ></DownloadDialog>
      <CardCommon
        title={propsTitle || title}
        className="dbo-community-card dbo-trends-card"
        hasDownload
        handleDownloadOnClick={() => setVisible(true)}
      >
        <>
          <div className="line-echarts">
            <LineChart
              colorObj={channelColorMap}
              tooltipFormatterFunc={(_params) => {
                const letTitle = listTimeTitleFunc(
                  _params[0]?.axisValue,
                  params.date_type,
                );
                const rightTitle = formatMessage({
                  id: metricsMap[metric] || NO_DATE.EMPTY,
                });
                const config = formatChartParamsDate({
                  title: letTitle,
                  rightTitle,
                  params: _params,
                  textShowFun: (marker: string, serName: any) => {
                    serName =
                      serName == 'facebook' ? 'facebook_gaming' : serName;
                    const { img, name } = channelInfo(serName);
                    return `<span class="chart-common-formatter-marker"> ${marker}<img src=${img} class="chart-common-formatter-img" /></span>&nbsp;${name || serName}`;
                  },
                  dealwithRecords: (records, total) => {
                    records.unshift({
                      text: formatMessage({ id: 'Sum' }),
                      value: mathFormat(total),
                    });
                  },
                });

                return config;
              }}
              xAxisData={lineXAxisData}
              seriesDataArray={lineSeriesData}
              lineLegendFormatter={(_name: string) => {
                const name = channelNameMap[_name];
                return `{${_name === 'facebook' ? 'facebook_gaming' : _name}|} ${name || _name}`;
              }}
              loading={loading}
              dateType={params.date_type}
              height={350}
              isStack
            ></LineChart>
          </div>
        </>
      </CardCommon>
    </>
  );
}
